// Hotel-Edition module "Instagram-Fotos" (PLAN.md §7.3, part B1): Instagram photo and carousel posts as
// cards in the company's image format, detail with caption, hashtags, Rubrik and date, edit/delete while
// allowed, approval via the weekly plan.
import { useQuery } from "@tanstack/react-query"
import { Instagram, Images } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { statusConfig } from "@/lib/status-config"
import { MEDIA_FORMAT_LABELS, effectiveMediaFormat } from "@shared/pillarFormats"
import { HotelPostDialog, postDateLabel } from "@/components/instagram/HotelPostDialog"
import { useHotelPostList } from "@/components/instagram/useHotelPostList"
import { EDIT_HINT } from "@/components/instagram/hotelPosts"
import { hasSlides } from "@/components/PostSlides"

const ASPECT_CLASS: Record<string, string> = {
  "1:1": "aspect-square",
  "4:5": "aspect-[4/5]",
  "3:4": "aspect-[3/4]",
  "4:3": "aspect-[4/3]",
  "16:9": "aspect-video",
  "9:16": "aspect-[9/16]",
}

export default function InstagramFotos() {
  const { data: company } = useQuery<{ dfyImageAspectRatio?: string | null }>({ queryKey: ["/api/companies/me"] })
  const aspect = ASPECT_CLASS[company?.dfyImageAspectRatio ?? "4:5"] ?? ASPECT_CLASS["4:5"]
  const { posts, total, isLoading, error, hasNextPage, fetchNextPage, isFetchingNextPage, selected, setSelected } =
    useHotelPostList("instagram-fotos", { platform: "instagram", mediaFormat: "foto,karussell" })

  return (
    <div className="container mx-auto p-6 space-y-6" data-testid="page-instagram-fotos">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Instagram className="h-8 w-8 text-pink-600" />
          Instagram-Fotos
        </h1>
        <p className="text-muted-foreground mt-2">
          Ihre Foto- und Karussell-Beiträge für Instagram. {EDIT_HINT}
        </p>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className={`${aspect} rounded-xl`} />)}
        </div>
      ) : error ? (
        <Card className="border-destructive">
          <CardContent className="pt-6 text-destructive">Die Beiträge konnten nicht geladen werden. Bitte laden Sie die Seite neu.</CardContent>
        </Card>
      ) : posts.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Images className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Noch keine Instagram-Fotos</h3>
            <p className="text-muted-foreground">Sobald Ihr Team die Beiträge der Woche vorbereitet hat, erscheinen sie hier.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <p className="text-sm text-muted-foreground" data-testid="text-result-count">{posts.length} von {total} Beiträgen</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {posts.map((post) => {
              const status = statusConfig[post.status as keyof typeof statusConfig] || statusConfig.pending
              return (
                <button
                  key={post.id}
                  type="button"
                  className="text-left rounded-xl border bg-card overflow-hidden hover:shadow-md transition-shadow"
                  onClick={() => setSelected(post)}
                  data-testid={`card-instagram-foto-${post.id}`}
                >
                  <div className={`relative ${aspect} bg-muted`}>
                    {post.imageUrl
                      ? <img src={post.imageUrl} alt={post.title || "Beitragsbild"} className="w-full h-full object-cover" loading="lazy" />
                      : <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">Kein Bild</div>}
                    <Badge className="absolute top-2 left-2 bg-white/90 text-foreground" data-testid={hasSlides(post) ? `badge-slides-${post.id}` : undefined}>
                      {MEDIA_FORMAT_LABELS[effectiveMediaFormat(post)]}{hasSlides(post) ? `, ${post.imageUrls.length} Bilder` : ""}
                    </Badge>
                  </div>
                  <div className="p-3 space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-muted-foreground truncate">{post.pillarName}</span>
                      <Badge className={status.color}>{status.label}</Badge>
                    </div>
                    <p className="text-sm line-clamp-2">{post.postContent}</p>
                    <p className="text-xs text-muted-foreground">{postDateLabel(post)}</p>
                  </div>
                </button>
              )
            })}
          </div>
          {hasNextPage && (
            <div className="flex justify-center">
              <Button variant="outline" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                {isFetchingNextPage ? "Lädt…" : "Mehr laden"}
              </Button>
            </div>
          )}
        </>
      )}

      <HotelPostDialog
        post={selected}
        open={!!selected}
        onOpenChange={(open) => { if (!open) setSelected(null) }}
        imageAspectClass={aspect}
      />
    </div>
  )
}
